/**
 * Parental gate (docs/MONETIZATION.md § 4): shown before every purchase and
 * restore. Two steps an adult does easily and a young child can't do by
 * tapping around:
 *
 *  1. press and hold a button for 3 s — it fills while held (no countdown
 *     text, no ticking); letting go resets it. Space / Enter held down works too.
 *  2. type the number written in words ("forty-seven") as digits. A wrong
 *     answer gets a gentle message and a new number; the third wrong answer
 *     in a row locks the gate for a minute (saved, so reopening doesn't help).
 *
 * The logic lives in services/parentalGate.ts; this is the UI. A pass is good
 * for one action: callers request the gate again for the next one. It guards
 * against accidental taps; the platform store's own approval (Ask to Buy,
 * Family Link) is the real barrier for money.
 */
import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import {
  checkGateAnswer,
  gateLockRemaining,
  holdProgress,
  loadGateLock,
  makeGateChallenge,
  OPEN_GATE_LOCK,
  recordGateFailure,
  saveGateLock,
  type GateChallenge,
  type GateLock,
} from '../services/parentalGate.ts';
import { randomSeed } from '../services/random.ts';
import { getLocale, useT } from '../i18n/index.ts';
import { Button } from './Button.tsx';
import { Dialog } from './Dialog.tsx';
import { Icon } from './Icon.tsx';
import './ui.css';

export interface ParentalGateProps {
  onPass(): void;
  onCancel(): void;
}

export function ParentalGate({ onPass, onCancel }: ParentalGateProps) {
  const t = useT();
  const titleId = useId();
  const [stage, setStage] = useState<'hold' | 'type'>('hold');
  const [lock, setLock] = useState<GateLock>(() => loadGateLock(Date.now()));
  const locked = gateLockRemaining(lock, Date.now()) > 0;

  // Re-open by itself when the lockout runs out (no countdown on screen).
  useEffect(() => {
    const remaining = gateLockRemaining(lock, Date.now());
    if (remaining === 0) return;
    const timer = setTimeout(() => setLock(loadGateLock(Date.now())), remaining + 50);
    return () => clearTimeout(timer);
  }, [lock]);

  const pass = () => {
    saveGateLock(OPEN_GATE_LOCK);
    onPass();
  };

  /** A wrong answer; returns true when it locked the gate. */
  const wrong = (): boolean => {
    const next = recordGateFailure(lock, Date.now());
    saveGateLock(next);
    setLock(next);
    const nowLocked = gateLockRemaining(next, Date.now()) > 0;
    if (nowLocked) setStage('hold');
    return nowLocked;
  };

  return (
    <Dialog onClose={onCancel} labelledBy={titleId} className="ui-gate">
      <div className="ui-dialog__head">
        <Icon name="lock" size="1.6em" className="ui-gate__icon" />
        <h2 id={titleId} className="ui-dialog__title">
          {t('shell.gateTitle')}
        </h2>
      </div>
      <p className="ui-dialog__text">{t('shell.gateIntro')}</p>
      {locked ? (
        <p className="ui-gate__message ui-gate__locked" role="status">
          {t('shell.gateLocked')}
        </p>
      ) : stage === 'hold' ? (
        <HoldStep onDone={() => setStage('type')} />
      ) : (
        <NumberStep onPass={pass} onWrong={wrong} />
      )}
      <div className="ui-dialog__actions">
        <Button variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </Dialog>
  );
}

/** Step 1: press and hold until the button is full. */
function HoldStep({ onDone }: { onDone(): void }) {
  const t = useT();
  const [progress, setProgress] = useState(0);
  const startRef = useRef<number | null>(null);
  const frameRef = useRef(0);

  const stop = useCallback(() => {
    startRef.current = null;
    cancelAnimationFrame(frameRef.current);
    setProgress(0);
  }, []);

  const start = useCallback(() => {
    if (startRef.current !== null) return;
    const began = performance.now();
    startRef.current = began;
    const tick = () => {
      if (startRef.current !== began) return;
      const p = holdProgress(performance.now() - began);
      setProgress(p);
      if (p >= 1) {
        startRef.current = null;
        onDone();
      } else {
        frameRef.current = requestAnimationFrame(tick);
      }
    };
    frameRef.current = requestAnimationFrame(tick);
  }, [onDone]);

  useEffect(() => () => cancelAnimationFrame(frameRef.current), []);

  return (
    <div className="ui-gate__step">
      <p className="ui-gate__instruction">{t('shell.gateHold')}</p>
      <button
        type="button"
        className="ui-gate__hold"
        style={{ '--hold': progress } as CSSProperties}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          start();
        }}
        onPointerUp={stop}
        onPointerCancel={stop}
        onLostPointerCapture={stop}
        onKeyDown={(e) => {
          if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) {
            e.preventDefault();
            start();
          }
        }}
        onKeyUp={(e) => {
          if (e.key === ' ' || e.key === 'Enter') stop();
        }}
        onBlur={stop}
        onContextMenu={(e) => e.preventDefault()}
      >
        <span className="ui-gate__hold-fill" aria-hidden="true" />
        <span className="ui-gate__hold-text">
          <Icon name="hand" />
          {t('shell.gateHoldButton')}
        </span>
      </button>
    </div>
  );
}

/** Step 2: type the number shown in words. `onWrong` returns true when the gate is now locked. */
function NumberStep({ onPass, onWrong }: { onPass(): void; onWrong(): boolean }) {
  const t = useT();
  const inputId = useId();
  const [challenge, setChallenge] = useState<GateChallenge>(() => makeGateChallenge(getLocale(), randomSeed()));
  const [answer, setAnswer] = useState('');
  const [wrong, setWrong] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => inputRef.current?.focus(), []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (checkGateAnswer(challenge, answer)) {
      onPass();
      return;
    }
    if (onWrong()) return; // locked: the gate shows the lockout message instead of this step
    setWrong(true);
    setAnswer('');
    setChallenge(makeGateChallenge(getLocale(), randomSeed()));
    inputRef.current?.focus();
  };

  return (
    <form className="ui-gate__step" onSubmit={submit}>
      <label className="ui-gate__instruction" htmlFor={inputId}>
        {t('shell.gateType')}
      </label>
      <p className="ui-gate__words" lang={challenge.locale}>
        {challenge.words}
      </p>
      <div className="ui-gate__answer">
        <input
          id={inputId}
          ref={inputRef}
          className="ui-input ui-gate__input"
          inputMode="numeric"
          autoComplete="off"
          maxLength={4}
          value={answer}
          onChange={(e) => setAnswer(e.currentTarget.value)}
        />
        <Button type="submit" variant="primary" icon="check" disabled={answer.trim() === ''}>
          {t('shell.gateCheck')}
        </Button>
      </div>
      <p className="ui-gate__message" role="status">
        {wrong ? t('shell.gateWrong') : ''}
      </p>
    </form>
  );
}

// ───────────── Hook ─────────────

export interface ParentalGateHandle {
  /** Resolves true when the gate was passed, false when cancelled. */
  requestGate(): Promise<boolean>;
  /** Render this somewhere in the tree (it is null while no gate is open). */
  gate: ReactNode;
}

/** Promise-style access to the gate for flows like "gate → purchase". */
export function useParentalGate(): ParentalGateHandle {
  const resolverRef = useRef<((passed: boolean) => void) | null>(null);
  const [open, setOpen] = useState(false);

  const finish = useCallback((passed: boolean) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setOpen(false);
    resolve?.(passed);
  }, []);

  const requestGate = useCallback(
    () =>
      new Promise<boolean>((resolve) => {
        resolverRef.current?.(false); // only one gate at a time
        resolverRef.current = resolve;
        setOpen(true);
      }),
    [],
  );

  // A request still open when the owner unmounts counts as cancelled.
  useEffect(
    () => () => {
      resolverRef.current?.(false);
      resolverRef.current = null;
    },
    [],
  );

  return {
    requestGate,
    gate: open ? <ParentalGate onPass={() => finish(true)} onCancel={() => finish(false)} /> : null,
  };
}
